import { useMemo } from 'react';

import { fetchWithTokenAndRegion } from '@adsk/acs-sc-web-platform-api';
import { useSWRAlloy } from '@adsk/alloy-helpers';
import { useSubscriptions } from '@adsk/alloy-provider';

import {
  LICENSES_TYPES,
  SUBSCRIPTION_STATUS,
  SUBSCRIPTION_TYPES,
} from '../helpers/const';
import { Offering, User } from '../helpers/types';
import {
  buildGetUserSubscriptionsRequest,
  isPaidSubscriptionOnOffering,
  isPaidSubscriptionOnOfferingV2,
  shouldShowExpiredMessage,
  transformUserSubscriptionsData,
} from '../helpers/utils';

type useUserSubscriptions = {
  user?: User | null;
  cleanLicenses: boolean;
  cleanLicensesLoading: boolean;
};

const useUserSubscriptions = ({
  user,
  cleanLicenses,
  cleanLicensesLoading,
}: useUserSubscriptions) => {
  const url = useMemo(() => {
    if (cleanLicensesLoading) return null;
    if (cleanLicenses) return null;
    if (!user) return null;

    const hasTrials = user.licenses?.some(
      ({ type }) => type === LICENSES_TYPES.TRIAL
    );

    return hasTrials ? buildGetUserSubscriptionsRequest() : null;
  }, [cleanLicenses, cleanLicensesLoading, user]);

  const { data: userSubscriptionsDataV1, error: userSubscriptionsErrorV1 } =
    useSWRAlloy(url, fetchWithTokenAndRegion);

  const { data: userSubscriptionsDataV2, error: userSubscriptionsErrorV2 } =
    useSubscriptions(cleanLicenses);

  const { data: userSubscriptionsData, error: userSubscriptionsError } =
    useMemo(() => {
      if (cleanLicensesLoading) return {};

      const data = cleanLicenses
        ? userSubscriptionsDataV2
        : userSubscriptionsDataV1;
      const error = cleanLicenses
        ? userSubscriptionsErrorV2
        : userSubscriptionsErrorV1;

      return { data, error };
    }, [
      userSubscriptionsDataV1,
      userSubscriptionsDataV2,
      userSubscriptionsErrorV1,
      userSubscriptionsErrorV2,
      cleanLicensesLoading,
      cleanLicenses,
    ]);

  const userTrialSubscriptions = useMemo(() => {
    if (userSubscriptionsError) return null;
    if (!userSubscriptionsData) return null;

    const subscriptions = cleanLicenses
      ? transformUserSubscriptionsData(userSubscriptionsData)
      : userSubscriptionsData.results;

    const isPaidSubscription = (subscription: Offering) => {
      return cleanLicenses
        ? isPaidSubscriptionOnOfferingV2(subscription, subscriptions)
        : isPaidSubscriptionOnOffering(subscription.products, user?.licenses);
    };

    return subscriptions
      .filter(
        (subscription: Offering) =>
          subscription.type === SUBSCRIPTION_TYPES.TRIAL &&
          !isPaidSubscription(subscription)
      )
      .sort(
        (a: Offering, b: Offering) =>
          a.offeringCategoryRanking - b.offeringCategoryRanking
      );
  }, [cleanLicenses, user, userSubscriptionsData, userSubscriptionsError]);

  const showBanner = useMemo(() => {
    if (userSubscriptionsError) return null;
    if (!userTrialSubscriptions || !user) return null;
    if (userTrialSubscriptions.length === 0) return null;

    const noActiveTrials = userTrialSubscriptions.every(
      (subscription: Offering) =>
        subscription.status === SUBSCRIPTION_STATUS.EXPIRED
    );

    if (noActiveTrials) {
      return shouldShowExpiredMessage(userTrialSubscriptions);
    }

    return true;
  }, [user, userSubscriptionsError, userTrialSubscriptions]);

  return {
    userTrialSubscriptions,
    userSubscriptionsError,
    showBanner,
  };
};

export default useUserSubscriptions;
